import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Search, Calendar, CheckCircle2, XCircle, RefreshCw, BarChart2 } from "lucide-react";
import { fetchCampagnesRequest } from "../../../redux/actions/campagnesActions";
import "./PerformanceConsolidation.css";

export default function PerformanceConsolidationList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const campaignsState = useSelector(s => s.campagnes || {});
    const campaigns = campaignsState.list || [];
    const loading = campaignsState.loading;

    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("ALL"); // ALL, ACTIVE, INACTIVE

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    // Filter to only get PERFORMANCE campaigns
    const performanceCampaigns = useMemo(() => {
        return (campaigns || []).filter(c => c.typeCampagne === "PERFORMANCE");
    }, [campaigns]);

    const filteredList = useMemo(() => {
        return performanceCampaigns.filter(c => {
            const matchesSearch = 
                c.nom?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                c.reference?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesStatus = filterStatus === "ALL" || 
                                 (filterStatus === "ACTIVE" && c.status) || 
                                 (filterStatus === "INACTIVE" && !c.status);
            
            return matchesSearch && matchesStatus;
        });
    }, [performanceCampaigns, searchTerm, filterStatus]);

    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString("fr-FR");
    };

    const handleSelectCampaign = (id) => {
        navigate(`/performance/rh/consolidation/${id}`);
    };

    return (
        <div className="perf-cons-container">
            <div className="perf-cons-header">
                <div className="perf-cons-header-info">
                    <h1>Consolidation Performance</h1>
                    <p>Sélectionnez une campagne de performance pour afficher le tableau de bord de consolidation.</p>
                </div>
            </div>

            {/* Toolbar for search and filters */}
            <div className="perf-cons-toolbar">
                <div className="perf-cons-search-container">
                    <Search size={16} className="perf-cons-search-icon" />
                    <input 
                        type="text" 
                        className="perf-cons-search-input" 
                        placeholder="Rechercher une campagne par nom ou référence..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                    <button 
                        className={`perf-cons-page-btn ${filterStatus === "ALL" ? "active" : ""}`}
                        style={filterStatus === "ALL" ? { backgroundColor: "#204385", color: "#fff", borderColor: "#204385" } : {}}
                        onClick={() => setFilterStatus("ALL")}
                    >
                        Toutes ({performanceCampaigns.length})
                    </button>
                    <button 
                        className={`perf-cons-page-btn ${filterStatus === "ACTIVE" ? "active" : ""}`}
                        style={filterStatus === "ACTIVE" ? { backgroundColor: "#10b981", color: "#fff", borderColor: "#10b981" } : {}}
                        onClick={() => setFilterStatus("ACTIVE")}
                    >
                        Actives ({performanceCampaigns.filter(c => c.status).length})
                    </button>
                    <button 
                        className={`perf-cons-page-btn ${filterStatus === "INACTIVE" ? "active" : ""}`}
                        style={filterStatus === "INACTIVE" ? { backgroundColor: "#ef4444", color: "#fff", borderColor: "#ef4444" } : {}}
                        onClick={() => setFilterStatus("INACTIVE")}
                    >
                        Inactives ({performanceCampaigns.filter(c => !c.status).length})
                    </button>
                </div>
            </div>

            {/* Campaign Grid List */}
            {loading && performanceCampaigns.length === 0 ? (
                <div className="perf-state perf-state--inline" style={{ textAlign: "center", padding: "40px" }}>
                    <RefreshCw size={24} className="animate-spin" style={{ margin: "0 auto 12px", color: "#204385" }} />
                    Chargement des campagnes de performance...
                </div>
            ) : filteredList.length === 0 ? (
                <div className="perf-state perf-state--warning">
                    Aucune campagne de performance disponible.
                </div>
            ) : (
                <div className="perf-cons-grid">
                    {filteredList.map(c => (
                        <div 
                            key={c.id} 
                            className={`perf-cons-card ${c.status ? "active" : ""}`}
                            onClick={() => handleSelectCampaign(c.id)}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                                <span className="perf-cons-card-ref">{c.reference || "SANS-REF"}</span>
                                {c.status ? (
                                    <span className="status-badge COMPLETED" style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                        <CheckCircle2 size={12} /> Active
                                    </span>
                                ) : (
                                    <span className="status-badge NOT_STARTED" style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                        <XCircle size={12} /> Inactive
                                    </span>
                                )}
                            </div>

                            <h2 className="perf-cons-card-title">{c.nom}</h2>
                            <p style={{ margin: "0 0 12px 0", fontSize: "12px", color: "#64748b" }}>
                                Année de campagne : <strong>{c.anneeCampagne}</strong>
                            </p>

                            <div className="perf-cons-card-dates">
                                <Calendar size={13} />
                                <span>{formatDate(c.dateDebut)} — {formatDate(c.dateFin)}</span>
                            </div>

                            <div className="perf-cons-card-footer">
                                <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                                    Objectifs max: {c.nombreMaxObjectifs || 0} ({c.nombreObjectifsQuantitatifs || 0} Qt / {c.nombreObjectifsQualitatifs || 0} Ql)
                                </span>
                                <span style={{ color: "#204385", display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: "700" }}>
                                    Consolider <BarChart2 size={14} />
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
